import { useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Button,
  Badge,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { OneShotSecretReveal } from '../../components/OneShotSecretReveal';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { isApiError } from '../../api/errors';
import {
  useMfaStatus,
  useMfaSetup,
  useMfaVerify,
  useMfaDisable,
  useRegenerateBackupCodes,
} from './useMe';

/**
 * Schema-Whitelist für die provisioning URL aus dem MFA-Setup.
 * Verhindert javascript:-URLs falls Core mal etwas Unerwartetes liefert.
 */
function safeOtpauthUrl(url) {
  if (typeof url !== 'string') return '#';
  const lower = url.trim().toLowerCase();
  if (lower.startsWith('otpauth:') || lower.startsWith('https:')) return url;
  return '#';
}

/**
 * MFA-Setup, Backup-Codes, Disable-Flow.
 * Quelle: 01_apis/01_core.api.valitype.net/ADMIN/views.md → #/security
 */
export function MeSecurityPage() {
  const status = useMfaStatus();

  if (status.isLoading) return <QueryLoadingState />;
  if (status.isError) return <QueryErrorState error={status.error} onRetry={() => status.refetch()} />;

  const data = status.data;

  return (
    <div>
      <Card className="mb-3">
        <CardBody>
          <CardTitle tag="h5">Zwei-Faktor-Authentifizierung</CardTitle>
          <p className="text-muted small">
            MFA schützt deinen Operator-Account gegen Credential-Stealing. Pflicht für{` `}
            <code>admin</code>-Role (per App-Setting <code>MFA_REQUIRED_FOR_ADMIN</code>).
          </p>

          {data.unavailable ? (
            <Alert color="info" className="small mb-0">
              MFA-Endpoint ist auf diesem Core-Deploy nicht aktiv
              (<code>GET /v1/operator/me/mfa</code> → 404). Setup/Disable sind
              nicht verfügbar, bis Core den Endpoint freischaltet.
            </Alert>
          ) : data.enabled ? (
            <MfaEnabledState status={data} />
          ) : (
            <MfaDisabledState />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <CardTitle tag="h5">Passwort-Historie</CardTitle>
          {data.passwordChangedAt ? (
            <p className="mb-0 small">
              Zuletzt geändert: <RelativeTime iso={data.passwordChangedAt} />
            </p>
          ) : (
            <p className="text-muted small mb-0">Keine Änderungs-Historie verfügbar.</p>
          )}
          <p className="text-muted small mt-2 mb-0">
            Passwort-Änderung im Tab "Profil" unter "Passwort ändern".
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

/* ── MFA NICHT aktiv → Setup-CTA ────────────────────────────────────── */

function MfaDisabledState() {
  const [setupOpen, setSetupOpen] = useState(false);

  return (
    <>
      <Alert color="warning" className="small">
        <strong>MFA ist aktuell NICHT aktiviert.</strong> Empfehlung: einmal einrichten,
        dann brauchst du beim Login einen 6-stelligen Code aus deiner Authenticator-App.
      </Alert>
      <Button color="primary" onClick={() => setSetupOpen(true)}>
        🛡️ MFA jetzt einrichten
      </Button>

      {setupOpen && (
        <MfaSetupFlow key="setup" onClose={() => setSetupOpen(false)} />
      )}
    </>
  );
}

function MfaSetupFlow({ onClose }) {
  // Stage 1: Setup laden (provisioning-URL + secret)
  // Stage 2: Code eingeben + verify
  // Stage 3: Backup-Codes via OneShotSecretReveal
  const setup = useMfaSetup();
  const verify = useMfaVerify();
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);

  // Sofort Setup starten beim Mount
  useEffectOnce(() => {
    setup
      .mutateAsync()
      .then(setSetupData)
      .catch((err) => setError(isApiError(err) ? err.message : 'Setup fehlgeschlagen.'));
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await verify.mutateAsync({ code });
      setBackupCodes(res.backupCodes);
    } catch (err) {
      if (isApiError(err) && err.code === 'mfa_invalid_code') {
        setError('Code ungültig — versuche es erneut.');
      } else {
        setError(isApiError(err) ? err.message : 'Verify fehlgeschlagen.');
      }
    }
  };

  if (backupCodes) {
    return (
      <OneShotSecretReveal
        isOpen
        label={`${backupCodes.length} Backup-Codes`}
        secret={backupCodes.join('\n')}
        confirmPhrase="Backup-Codes sicher verstaut — jeder ist genau einmal verwendbar"
        onAcknowledged={onClose}
      />
    );
  }

  return (
    <Modal isOpen toggle={setup.isPending || verify.isPending ? undefined : onClose} backdrop="static" size="lg">
      <ModalHeader toggle={setup.isPending || verify.isPending ? undefined : onClose}>
        MFA einrichten
      </ModalHeader>
      <ModalBody>
        {setup.isPending && !setupData && (
          <div className="text-center py-4">
            <div className="spinner-border spinner-border-sm me-2" aria-hidden />
            Provisioning vorbereiten…
          </div>
        )}

        {setupData && (
          <>
            <h6 className="text-uppercase small text-muted">Schritt 1 — Authenticator hinzufügen</h6>
            <p className="small mb-2">
              Öffne deine Authenticator-App (Google Authenticator, Authy, 1Password, Bitwarden, …) und scanne
              den QR oder gib den Secret manuell ein.
            </p>

            <div className="text-center my-3 p-3" style={{ background: 'var(--bs-tertiary-bg)', borderRadius: 6 }}>
              <a
                href={safeOtpauthUrl(setupData.provisioningUrl)}
                className="d-block fw-semibold mb-2"
                style={{ wordBreak: 'break-all' }}
              >
                📱 Auf Mobile: tippen zum Direkt-Öffnen
              </a>
              <div className="small text-muted">otpauth-URL (für QR-Generator oder Auto-Import):</div>
              <CopyChip value={setupData.provisioningUrl} label="Provisioning-URL" />
            </div>

            <div className="text-center my-3">
              <div className="small text-muted text-uppercase">Manueller Secret-Key</div>
              <CopyChip value={setupData.secret} label="MFA-Secret" />
              <div className="small text-muted mt-1">
                Algorithmus: SHA1 · 6 Stellen · 30s Period
              </div>
            </div>

            <hr />

            <h6 className="text-uppercase small text-muted">Schritt 2 — Code verifizieren</h6>
            <Form onSubmit={onSubmit}>
              <FormGroup>
                <Label className="small">Aktueller 6-stelliger Code aus der App</Label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  style={{ fontFamily: 'monospace', fontSize: 18, letterSpacing: 4, textAlign: 'center' }}
                  autoComplete="one-time-code"
                />
              </FormGroup>
              {error && <Alert color="danger" className="small mb-2">{error}</Alert>}
              <Button color="primary" type="submit" disabled={code.length !== 6 || verify.isPending}>
                {verify.isPending ? 'Verifizieren…' : 'Aktivieren'}
              </Button>
            </Form>
          </>
        )}

        {error && !setupData && <Alert color="danger">{error}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={setup.isPending || verify.isPending}>
          Abbrechen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/* ── MFA AKTIV → Disable + Regenerate ───────────────────────────────── */

function MfaEnabledState({ status }) {
  const [pendingRegen, setPendingRegen] = useState(false);
  const [pendingDisable, setPendingDisable] = useState(false);
  const [regenCodes, setRegenCodes] = useState(null);
  const regenerate = useRegenerateBackupCodes();
  const disable = useMfaDisable();

  return (
    <>
      <div className="d-flex align-items-center gap-3 mb-3">
        <Badge color="success" pill style={{ fontSize: 14, padding: '6px 12px' }}>✓ MFA aktiv</Badge>
        <span className="text-muted small">
          Seit <RelativeTime iso={status.enabledAt} />
        </span>
      </div>

      <p className="small text-muted">
        Beim Login wird ein 6-stelliger Code aus der Authenticator-App + Backup-Code als
        Fallback verlangt.
      </p>

      <div className="d-flex gap-2 flex-wrap">
        <Button color="info" outline onClick={() => setPendingRegen(true)}>
          🔄 Backup-Codes regenerieren
        </Button>
        <Button color="danger" outline onClick={() => setPendingDisable(true)}>
          🛑 MFA deaktivieren
        </Button>
      </div>

      <RegenerateBackupCodesDialog
        isOpen={pendingRegen}
        onClose={() => setPendingRegen(false)}
        onResult={(codes) => {
          setPendingRegen(false);
          setRegenCodes(codes);
        }}
        regenerate={regenerate}
      />

      {regenCodes && (
        <OneShotSecretReveal
          isOpen
          label={`${regenCodes.length} neue Backup-Codes`}
          secret={regenCodes.join('\n')}
          confirmPhrase="Neue Backup-Codes sicher verstaut · alte sind sofort ungültig"
          onAcknowledged={() => setRegenCodes(null)}
        />
      )}

      <DisableMfaDialog
        isOpen={pendingDisable}
        onClose={() => setPendingDisable(false)}
        disable={disable}
      />
    </>
  );
}

function RegenerateBackupCodesDialog({ isOpen, onClose, onResult, regenerate }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await regenerate.mutateAsync({ currentPassword, totpCode });
      setCurrentPassword('');
      setTotpCode('');
      onResult(res.backupCodes);
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Regenerieren fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={regenerate.isPending ? undefined : onClose}>
      <ModalHeader toggle={regenerate.isPending ? undefined : onClose}>
        Backup-Codes regenerieren
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Alert color="warning" className="small">
            <strong>Alle bisherigen Backup-Codes werden sofort ungültig.</strong>{` `}
            Die neuen werden genau einmal angezeigt — verstaue sie sicher (Password-Manager).
          </Alert>
          <FormGroup>
            <Label className="small">Aktuelles Passwort</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Aktueller TOTP-Code</Label>
            <Input
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              style={{ fontFamily: 'monospace', letterSpacing: 4, textAlign: 'center' }}
              required
            />
          </FormGroup>
          {error && <Alert color="danger" className="small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={regenerate.isPending}>
            Abbrechen
          </Button>
          <Button color="info" type="submit" disabled={!currentPassword || totpCode.length !== 6 || regenerate.isPending}>
            {regenerate.isPending ? 'Regenerieren…' : 'Neue Codes generieren'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function DisableMfaDialog({ isOpen, onClose, disable }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await disable.mutateAsync({ currentPassword, totpCode });
      setCurrentPassword('');
      setTotpCode('');
      onClose();
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Deaktivieren fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={disable.isPending ? undefined : onClose}>
      <ModalHeader toggle={disable.isPending ? undefined : onClose}>
        MFA deaktivieren
      </ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Alert color="danger" className="small">
            <strong>Sicherheitsrelevant:</strong> Nach Deaktivierung ist dein Account nur noch
            durch Passwort geschützt. Bei admin-Role meckert{` `}
            <code>MFA_REQUIRED_FOR_ADMIN</code> u. U. beim nächsten Login.
          </Alert>
          <FormGroup>
            <Label className="small">Aktuelles Passwort</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label className="small">Aktueller TOTP-Code</Label>
            <Input
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              style={{ fontFamily: 'monospace', letterSpacing: 4, textAlign: 'center' }}
              required
            />
          </FormGroup>
          {error && <Alert color="danger" className="small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={disable.isPending}>
            Abbrechen
          </Button>
          <Button color="danger" type="submit" disabled={!currentPassword || totpCode.length !== 6 || disable.isPending}>
            {disable.isPending ? 'Deaktivieren…' : 'MFA endgültig deaktivieren'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

/* ── Helper ─────────────────────────────────────────────────────────── */

import { useEffect, useRef } from 'react';
/** Run callback once on mount — safe vs. React-Compiler lint */
function useEffectOnce(fn) {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
